import { Link } from "react-router-dom";
import Footer from "../components/Footer";
import PublicNav from "../components/PublicNav";
import { Check } from "lucide-react";

// ── Employer tiers ──────────────────────────────────────────────

const employerTiers = [
  {
    name: "Starter",
    plan: "starter",
    price: "€0",
    period: "forever",
    description: "Get started, post your first role, see what Joborigo does.",
    features: [
      "1 active job position",
      "Submission link with a real pipeline",
      "Manual status push",
    ],
  },
  {
    name: "Growth",
    plan: "growth",
    price: "€99",
    period: "per month",
    description: "Mid-size hiring teams running multiple roles.",
    gradient: true,
    features: [
      "Up to 10 active job positions",
      "Full lightweight ATS: positions linked directly to arena sends",
      "Arena sourcing: browse candidates, send positions, multi-select",
      "Editable positions with candidate notifications on material changes",
      "Correspondence: meetings, document requests, assessments",
      "Status push API + candidate linking API",
    ],
  },
  {
    name: "Scale",
    plan: "scale",
    price: "€249",
    period: "per month",
    description: "High-volume hiring at scale.",
    features: [
      "Unlimited active job positions",
      "Full lightweight ATS at scale, unlimited pipelines and candidates",
      "Everything in Growth, uncapped",
      "Priority support",
    ],
  },
];

// ── Agency tiers ────────────────────────────────────────────────

const agencyTiers = [
  {
    name: "Solo",
    plan: "solo",
    price: "€199",
    period: "per month",
    description: "Independent recruiters running their own pipelines.",
    features: [
      "1 recruiter",
      "Up to 10 active positions",
      "Up to 100 connected candidates",
      "Manual status push per candidate",
      "Client share view (branded)",
      "Per-client pipeline view",
    ],
  },
  {
    name: "Standard",
    plan: "standard",
    price: "€499",
    period: "per month",
    description: "Growing agencies with a team of recruiters.",
    gradient: true,
    features: [
      "Up to 5 recruiters",
      "Up to 50 active positions",
      "Up to 500 connected candidates",
      "Bulk status push",
      "Custom pipeline stages",
      "Saved arena searches",
      "Private notes + tags per candidate",
      "Priority support",
    ],
  },
  {
    name: "Pro",
    plan: "pro",
    price: "€1,199",
    period: "per month",
    description: "Established agencies with full operational scale.",
    features: [
      "Unlimited recruiters",
      "Unlimited active positions",
      "Unlimited connected candidates",
      "All Standard features",
      "Placement tracking + aggregate stats",
      "Per-client reporting (branded)",
      "Priority support",
    ],
  },
];

// ── Components ──────────────────────────────────────────────────

function TierCard({ tier }) {
  const ctaLabel = tier.plan === "starter" ? "Sign up free" : "Get started";

  const handleClick = () => {
    window.location.href = "/employers/register";
  };

  return (
    <div
      className={`rounded-2xl p-px ${
        tier.gradient
          ? "bg-brand-violet"
          : "border border-gray-200 dark:border-white/10"
      }`}
    >
      <div className="rounded-2xl bg-white dark:bg-brand-dark-surface p-6 h-full flex flex-col relative">
        {tier.gradient && (
          <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-brand-violet text-white text-xs font-semibold rounded-full whitespace-nowrap">
            Most popular
          </span>
        )}

        <div className="mb-6">
          <p className="text-sm font-medium text-gray-400 uppercase tracking-widest mb-1">
            {tier.name}
          </p>
          <div className="flex items-end gap-1 mb-1">
            <span className="text-3xl font-extrabold">{tier.price}</span>
            <span className="text-gray-400 text-sm mb-1">/ {tier.period}</span>
          </div>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-2">
            {tier.description}
          </p>
        </div>

        <button
          onClick={handleClick}
          className={`text-center py-2.5 rounded-lg text-sm font-medium transition mb-6 ${
            tier.gradient
              ? "bg-brand-violet text-white hover:opacity-90"
              : "border border-brand-violet dark:border-brand-violet-on-dark text-brand-violet dark:text-brand-violet-on-dark hover:bg-brand-violet-tint dark:hover:bg-brand-violet/10"
          }`}
        >
          {ctaLabel}
        </button>

        <ul className="flex flex-col gap-2.5 flex-1">
          {tier.features.map((f) => (
            <li
              key={f}
              className="flex items-start gap-2.5 text-sm text-gray-600 dark:text-gray-300"
            >
              <Check className="w-4 h-4 text-brand-violet dark:text-brand-violet-on-dark flex-shrink-0 mt-0.5" />
              {f}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ── Main ────────────────────────────────────────────────────────

export default function Pricing() {
  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-brand-dark-bg text-gray-900 dark:text-gray-100">
      <PublicNav />

      <main className="flex-1 max-w-6xl mx-auto px-6 py-16 w-full">
        {/* Header */}
        <div className="text-center mb-14">
          <p className="text-sm font-medium text-brand-violet dark:text-brand-violet-on-dark mb-3 uppercase tracking-widest">
            Pricing
          </p>
          <h1 className="text-4xl font-extrabold mb-4">
            Honest pricing for everyone in the hiring loop.
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-lg max-w-2xl mx-auto">
            No advertising. No data sales. Subscription pricing only. Pay what's
            fair for what you actually use.
          </p>
        </div>

        {/* Seeker banner — always free, no tier card needed */}
        <section className="mb-20 text-center">
          <p className="text-xs font-semibold text-brand-violet dark:text-brand-violet-on-dark uppercase tracking-widest mb-2">
            For job seekers
          </p>
          <h2 className="text-2xl md:text-3xl font-bold mb-3">
            Free, forever. No ads, no data selling, no paywalls.
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-5 max-w-xl mx-auto">
            Track every application, get honest status updates, and never get
            ghosted. Every feature, no subscription, ever.
          </p>
          <Link
            to="/register"
            className="inline-block bg-brand-violet text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:opacity-90 transition"
          >
            Sign up free
          </Link>
        </section>

        {/* Employer section */}
        <section className="mb-20">
          <div className="text-center mb-10">
            <p className="text-xs font-semibold text-brand-violet dark:text-brand-violet-on-dark uppercase tracking-widest mb-2">
              For employers
            </p>
            <h2 className="text-2xl md:text-3xl font-bold mb-3">
              Stop paying for a quarter of a tool you don't need.
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xl mx-auto">
              The only ATS where the candidate is already logged in. Start free,
              upgrade when you actually need more.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {employerTiers.map((tier) => (
              <TierCard key={tier.name} tier={tier} />
            ))}
          </div>
        </section>

        {/* Agency section */}
        <section className="mb-12">
          <div className="text-center mb-10">
            <p className="text-xs font-semibold text-brand-violet dark:text-brand-violet-on-dark uppercase tracking-widest mb-2">
              For recruitment agencies
            </p>
            <h2 className="text-2xl md:text-3xl font-bold mb-3">
              Your candidates stay yours. Your client relationships stay yours.
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xl mx-auto">
              The system of record for sourcing, pipeline management, and client
              share. Joborigo never inserts itself between you and your network.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {agencyTiers.map((tier) => (
              <TierCard key={tier.name} tier={tier} />
            ))}
          </div>
        </section>

        {/* FAQ nudge */}
        <div className="text-center text-sm text-gray-400 dark:text-gray-500 mb-6">
          Questions about pricing?{" "}
          <Link to="/about" className="text-brand-violet dark:text-brand-violet-on-dark hover:underline">
            Read more about Joborigo
          </Link>{" "}
          or{" "}
          <a
            href="mailto:info@joborigo.com"
            className="text-brand-violet dark:text-brand-violet-on-dark hover:underline"
          >
            get in touch
          </a>
          .
        </div>

        <p className="text-center text-xs text-gray-400 dark:text-gray-500 mb-6">
          All prices in EUR. VAT and local taxes added at checkout where
          applicable. Payments handled securely via Paddle.
        </p>
      </main>

      {/* Legal block */}
      <div className="text-center text-xs text-gray-400 dark:text-gray-500 pb-6 px-6">
        By subscribing you agree to our{" "}
        <Link to="/terms" className="text-brand-violet dark:text-brand-violet-on-dark hover:underline">
          Terms of Service
        </Link>{" "}
        and{" "}
        <Link to="/privacy" className="text-brand-violet dark:text-brand-violet-on-dark hover:underline">
          Privacy Policy
        </Link>
        .{" "}
        <Link to="/refund-policy" className="text-brand-violet dark:text-brand-violet-on-dark hover:underline">
          Refund Policy
        </Link>
        .
      </div>

      <Footer />
    </div>
  );
}
